import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import apiService from '../services/apiService';

interface CarDetails {
  brand: string;
  model: string;
  engine: string;
  icon: string;
  id_brand: number; // Added for API integration
}

interface CarSelectionProps {
  serviceType: 'simple' | 'complete';
  onNextStep: (carDetails: CarDetails) => void;
  onBackStep: () => void;
}

export default function CarSelection({ serviceType, onNextStep, onBackStep }: CarSelectionProps) {
  const { t } = useTranslation();
  const [selectedCar, setSelectedCar] = useState<CarDetails>({ 
    brand: '', 
    model: '', 
    icon: '',
    engine: '',
    id_brand: 0 
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState<'brand' | 'model' | 'engine' | null>(null);
  const [brands, setBrands] = useState<Array<{id: number, name: string}>>([]);
  const [models, setModels] = useState<Array<{id: number, modele: string, annee: string, id_marque: number}>>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [error, setError] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);

  // Fetch only brands on initial load
  useEffect(() => {
    const loadBrands = async () => {
      try {
        setLoadingBrands(true);
        const result = await apiService.getCarOptions();
        if (result.success) {
          setBrands(result.data.brands.map((b: any) => b.brand));
        } else {
          setError(t('car.loadError'));
        }
      } catch (err) {
        setError(t('car.loadError'));
        console.error(err);
      } finally {
        setLoadingBrands(false);
      }
    };
    loadBrands();
  }, [t]);

  // Fetch models only when a brand is selected
  useEffect(() => {
    if (!selectedBrandId) return;
    
    const loadModels = async () => {
      try {
        setLoadingModels(true);
        const result = await apiService.getModelsByBrand(selectedBrandId);
        if (result.success) {
          setModels(result.data);
        } else {
          setError(t('car.loadModelsError'));
        }
      } catch (err) {
        setError(t('car.loadModelsError'));
        console.error(err);
      } finally {
        setLoadingModels(false);
      }
    };
    
    loadModels();
  }, [selectedBrandId, t]);

  const handleBrandSelection = (brand: any) => {
    setSelectedCar({
      brand: brand.name,
      model: '',
      engine: '',
      icon:brand.icon,
      id_brand: 0
    });
    
    setSelectedBrandId(brand.id);
    setModels([]); // Clear previous models
    setIsDropdownOpen(null);
  };

  const handleModelSelection = (model: any) => {
    setSelectedCar(prev => ({
      ...prev,
      model: model.modele,
      engine: model.annee,
      id_brand: model.id
    }));
    setIsDropdownOpen(null);
  };

  const handleNext = () => {
    if (selectedCar.id_brand) {
      onNextStep(selectedCar);
    }
  };

  if (loadingBrands) {
    return (
      <div className="flex flex-col min-h-screen bg-[#ededed] items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#2196F3] mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-[#ededed] items-center justify-center">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-sm">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold mb-2">{t('car.errorTitle')}</h3>
          <p className="mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#2196F3] text-white rounded-full shadow-md"
          >
            {t('car.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#ededed]">
      <div className="flex flex-col items-center px-4 max-w-md mx-auto w-full">
        <button
          onClick={onBackStep}
          className="self-start relative top-12 mb-4 flex items-center text-blue-500"
          aria-label={t('common.back')}
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t('common.back')}
        </button>

        <h1 className="text-[32px] text-center font-light mb-4">
          {t('car.title')}
        </h1>
        <p className="text-center text-[16px] font-light mb-12">
          {t('car.subtitle')}
        </p>

        <div className="space-y-4 mb-8 w-full">
          {/* Brand Selection */}
          <div className="relative">
            <button
              className="w-full p-4 text-left bg-white border border-gray-200 rounded-lg flex justify-between items-center"
              onClick={() => setIsDropdownOpen(prev => prev === 'brand' ? null : 'brand')}
              aria-haspopup="listbox"
              aria-expanded={isDropdownOpen === 'brand'}
            >
              <span className="text-[16px] font-light">
                {selectedCar.brand || t('car.brand')}
              </span>
              <ChevronDown className="w-5 h-5" />
            </button>
            {isDropdownOpen === 'brand' && (
              <ul className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto" role="listbox">
                {brands.map((brand) => (
                  <li key={brand.id}>
                    <button
                      className="w-full p-4 text-left hover:bg-gray-100"
                      onClick={() => handleBrandSelection(brand)}
                      role="option"
                      aria-selected={selectedCar.brand === brand.name}
                    >
                      {brand.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Model Selection */}
          <div className="relative">
            <button
              className={`w-full p-4 text-left bg-white border border-gray-200 rounded-lg flex justify-between items-center ${
                !selectedCar.brand ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={() => selectedCar.brand && setIsDropdownOpen(prev => prev === 'model' ? null : 'model')}
              aria-haspopup="listbox"
              aria-expanded={isDropdownOpen === 'model'}
              disabled={!selectedCar.brand}
            >
              {loadingModels ? (
                <div className="flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-[16px] font-light text-gray-400">{t('common.loading')}</span>
                </div>
              ) : (
                <span className="text-[16px] font-light">
                  {selectedCar.model || t('car.model')}
                </span>
              )}
              <ChevronDown className="w-5 h-5" />
            </button>
            {isDropdownOpen === 'model' && (
              <ul className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto" role="listbox">
                {models.length === 0 ? (
                  <li className="p-4 text-center text-gray-500">
                    {loadingModels ? t('common.loading') : t('car.noModelsAvailable')}
                  </li>
                ) : (
                  models.map((model) => (
                    <li key={model.id}>
                      <button
                        className="w-full p-4 text-left hover:bg-gray-100"
                        onClick={() => handleModelSelection(model)}
                        role="option"
                        aria-selected={selectedCar.model === model.modele}
                      >
                        {model.modele} - {model.annee}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </div>

        {selectedCar.brand && selectedCar.model && selectedCar.engine && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 w-full">
            <div className="flex items-center space-x-4">
              <img
                src={`https://fauconbleu.ma/storage/${selectedCar.icon?.toLowerCase() }`}
                alt={selectedCar.brand}
                className="w-16 h-16 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/default_car_logo.png";
                }}
              />
              <div className="space-y-1">
                <p className="text-lg">
                  {selectedCar.brand} <span className="text-lg">{selectedCar.model}</span>{' '}
                  <span className="text-gray-600">{selectedCar.engine}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        <button 
          className={`w-[90%] py-4 px-6 border-[#2196F3] border-2 font-bold rounded-full bg-white text-[#2196F3] shadow-lg text-lg transition-colors ${
            !selectedCar.brand || !selectedCar.model || !selectedCar.engine 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-gray-50'
          }`}
          onClick={handleNext}
          disabled={!selectedCar.brand || !selectedCar.model || !selectedCar.engine}
        >
          {t('car.next')}
        </button>
      </div>
    </div>
  );
}